import { useEffect } from 'react'

import { FormattedMessage } from 'react-intl'

import { useNavigation } from '@subaio/components'
import { type Api, logError, RequestError } from '@subaio/utils'

import type { UserLogin } from 'apiTypes/UserLogin'

import { asContainer } from 'lib/container'

import { Expired } from '../Authentication/Expired'
import { getLocaleConfiguration, legacyLocaleConfiguration } from '../Authentication/localeConfiguration'
import { Unauthorized } from '../Authentication/Unauthorized'
import { OneTimeAuthenticationForm } from './AuthenticationForm'

type Params = {
	sessionId: string
	country: string
}

type State = {
	status: 'INITIAL' | 'INVALID_LOGIN' | 'INVALID_URL' | 'EXPIRED' | 'UNKNOWN_ERROR'
	localeOptions: Array<string>
}

const uuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/

const oneTimeLogin = (api: Api, sessionId: string, locale: string) =>
	api.post<UserLogin>('/users/one-time-login', { sessionId, locale })

export const container = asContainer<State, Params>()({
	path: '/:country/:sessionId/one-time-auth',
	authentication: { type: 'public' },
	initialize: async ({ sessionId, country, query, setLocale, setUser, updatePage, api }) => {
		const localeConfiguration = getLocaleConfiguration(country)

		if (!localeConfiguration) {
			return {
				status: 'INVALID_URL',
				localeOptions: legacyLocaleConfiguration.localeOptions,
			}
		}

		const localeOptions = localeConfiguration.localeOptions
		const selectedLocale = localeOptions.find((locale) => locale === query?.locale)
		const isValidSessionId = uuidRegex.test(sessionId)

		await setLocale(selectedLocale ?? localeConfiguration.defaultLocale)

		if (selectedLocale && isValidSessionId) {
			try {
				setUser({ ...(await oneTimeLogin(api, sessionId, selectedLocale)), sessionId, isOneTimeAuth: true })
				updatePage({ name: 'Flow' })
			} catch (error) {
				if (error instanceof RequestError && error.request.statusCode === 403) {
					return { status: 'EXPIRED', localeOptions }
				}
				if (error instanceof RequestError && error.request.statusCode === 401) {
					return { status: 'INVALID_LOGIN', localeOptions }
				}
				logError(error)
				return { status: 'UNKNOWN_ERROR', localeOptions }
			}
		}

		return {
			status: isValidSessionId ? 'INITIAL' : 'INVALID_URL',
			localeOptions,
		}
	},
	Running: ({ status, sessionId, setData, setUser, api, locale, setPageTitle, setLocale, localeOptions }) => {
		const { onNavigateTo } = useNavigation()
		useEffect(() => {
			switch (status) {
				case 'INITIAL':
				case 'INVALID_LOGIN':
				case 'UNKNOWN_ERROR': {
					setPageTitle(
						<FormattedMessage id='oneTimeAuthentication.title' defaultMessage='Get an overview of your finances' />,
					)
					break
				}
				case 'INVALID_URL': {
					setPageTitle(<FormattedMessage id='flow.unauthorized.title' defaultMessage='Link is invalid' />)
					break
				}
				case 'EXPIRED': {
					setPageTitle(
						<FormattedMessage id='flow.expired.title' defaultMessage='Link is expired or already completed' />,
					)
					break
				}
			}
		}, [setPageTitle, status])

		switch (status) {
			case 'INITIAL': // Fallthrough
			case 'UNKNOWN_ERROR':
			case 'INVALID_LOGIN':
				return (
					<OneTimeAuthenticationForm
						locale={locale}
						localeOptions={localeOptions}
						setLocale={setLocale}
						hasInvalidLogin={status === 'INVALID_LOGIN'}
						hasUnknownError={status === 'UNKNOWN_ERROR'}
						onSubmit={async () => {
							try {
								const loginResponse = await oneTimeLogin(api, sessionId, locale)
								setUser({ ...loginResponse, sessionId, isOneTimeAuth: true })
								onNavigateTo({ name: 'Flow' })
							} catch (error) {
								if (error instanceof RequestError) {
									if (error.request.statusCode === 403) {
										setData({ status: 'EXPIRED' })
										return
									}
									if (error.request.statusCode === 401) {
										setData({ status: 'INVALID_LOGIN' })
										return
									}
								}

								setData({ status: 'UNKNOWN_ERROR' })
								throw error
							}
						}}
					/>
				)
			case 'INVALID_URL':
				return <Unauthorized />
			case 'EXPIRED':
				return <Expired />
		}
	},
})
